import { useEffect, useRef } from 'react';

import type { CreditsBannerState } from '../../common/credits-ui-state/types';
import type { LocalStorageErrorHandler } from '../../common/storage/local-storage';
import { useDismissalPersistence } from '../credits-banner-content/utils';

export type CreditsSurfaceVisibility = {
	isVisible: boolean;
	isWarning: boolean;
	dismiss: () => void;
};

/**
 * Whether a credits surface should render, without its copy, so the popup can decide on its
 * trigger before its panel code loads. Only call it once credits state exists: dismissal
 * persistence clears a stored dismissal while the state is not a warning.
 */
export const useCreditsSurfaceVisibility = (
	state: CreditsBannerState,
	{ onDismiss, onError }: { onDismiss?: () => void; onError?: LocalStorageErrorHandler },
): CreditsSurfaceVisibility => {
	const isWarning = state.balanceState === 'warning';
	const variant = `${state.balanceState}-${state.depletionBehavior}-${state.scope}`;
	const previousVariant = useRef(variant);
	const { isDismissed, dismissBanner, showBanner } = useDismissalPersistence(isWarning, onError);

	useEffect(() => {
		if (variant !== previousVariant.current && isDismissed) {
			showBanner();
		}
		previousVariant.current = variant;
	}, [variant, isDismissed, showBanner]);

	return {
		isVisible: state.depletionBehavior !== null && !isDismissed,
		isWarning,
		dismiss: () => {
			dismissBanner();
			onDismiss?.();
		},
	};
};
